import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
// recast-navigation is ~800 KB of WASM — loaded DYNAMICALLY inside
// loadNavmesh() so users who never localize don't pay for it at startup.
import type { NavMesh, NavMeshQuery } from 'recast-navigation';
import { MultisetRestClient } from './multisetRestClient';
import { logNavmesh, logEvent } from './debugLog';
import type { NavMeConfig, Vec3 } from '../types';

/**
 * Navmesh routing via recast-navigation (WASM port of Recast/Detour — the
 * industry-standard navmesh toolset). Paths follow WALKABLE surfaces only:
 * floors, ramps, stair runs. Voids (stairwells), railings, walls and
 * furniture are excluded by generation, which is exactly what fixes
 * "breadcrumbs floating in the air over the stairwell".
 *
 * Navmesh source priority:
 *  1. Cached generated navmesh (localStorage, keyed by map code) — instant.
 *  2. Authored navmesh at public/navmesh.glb (Blender/Recast export in map
 *     space) — best quality if you ever want to hand-tune.
 *  3. AUTO-GENERATED from the MultiSet scan mesh (TexturedMesh.glb via the
 *     REST API) — no authoring needed; Recast voxelizes the scan and keeps
 *     only surfaces a person can stand on/walk between (slope, clearance,
 *     step height and agent radius constraints below).
 */

/** Height of the phone camera above the walkable surface (matches NavigationOverlay). */
export const CAMERA_HEIGHT_M = 1.4;
const CACHE_PREFIX = 'navme.navmesh.v6.'; // bump to force regeneration after config changes (v6: stairs — walkableClimb 0.2→0.35 m, slope 40→46°)

/**
 * Recast generation settings, tuned for indoor scans. Units:
 * cs/ch are voxel cell size/height in metres; walkableHeight/Climb/Radius
 * are in voxel units (metres ÷ ch or cs).
 */
export const GENERATION_CONFIG = {
  cs: 0.1, // 10 cm horizontal voxels — fine enough for corridors/door gaps
  ch: 0.1, // 10 cm vertical voxels
  walkableSlopeAngle: 46, // stair runs in SCANNED meshes read steeper than real (noise + smoothed nosings) — 40° left the venue's floors disconnected ("navmesh path is partial", 2026-07-12); walls (≈90°) still excluded
  walkableHeight: 18, // 1.8 m clearance required — excludes countertops/tables (≈1.4-1.5 m to ceiling) that 1.6 m let through (÷ch)
  walkableClimb: 3, // 0.3 m max step (÷ch) — scanned stair risers read taller than the real ~0.18 m; 0.2 m disconnected the stairwell (2026-07-12)
  walkableRadius: 3, // 0.3 m agent radius (÷cs) — 0.2 hugged walls so tightly that spline-smoothed crumbs clipped into them (user video, 2026-07-12); 0.3 still passes 0.8 m doorways with margin
  maxEdgeLen: 12,
  maxSimplificationError: 1.3,
  minRegionArea: 12, // prune small isolated islands (e.g. furniture tops) more aggressively
  mergeRegionArea: 20,
  maxVertsPerPoly: 6,
  detailSampleDist: 6,
  detailSampleMaxError: 1,
};

let query: NavMeshQuery | null = null;
let navMeshInstance: NavMesh | null = null;

/**
 * TEST SEAM: install an already-built navmesh + query. Unit tests build
 * synthetic venues in Node (tests/helpers/syntheticVenue.ts) and inject
 * them here so routeService/snap/path logic runs against geometry we fully
 * control. Production code never calls this — loadNavmesh() below owns the
 * real pipeline (cache → authored GLB → auto-generated from scan).
 */
export function installNavmesh(nm: NavMesh, q: NavMeshQuery): void {
  navMeshInstance = nm;
  query = q;
}

/** Unloads the navmesh (test isolation between synthetic venues). */
export function clearNavmesh(): void {
  navMeshInstance = null;
  query = null;
}

export async function loadGlbMeshes(url: string): Promise<THREE.Mesh[]> {
  const loader = new GLTFLoader();
  const draco = new DRACOLoader();
  draco.setDecoderPath('/draco/'); // self-hosted decoders (public/draco/)
  loader.setDRACOLoader(draco);
  try {
    const gltf = await loader.loadAsync(url);
    gltf.scene.updateMatrixWorld(true);
    const meshes: THREE.Mesh[] = [];
    gltf.scene.traverse((node) => {
      if ((node as THREE.Mesh).isMesh) meshes.push(node as THREE.Mesh);
    });
    return meshes;
  } finally {
    draco.dispose();
  }
}

/** Uint8Array <-> base64 without blowing the call stack on large arrays. */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function loadNavmesh(config: NavMeConfig): Promise<boolean> {
  try {
    const [{ init: initRecast, NavMeshQuery, exportNavMesh, importNavMesh }, { threeToSoloNavMesh }] =
      await Promise.all([import('recast-navigation'), import('@recast-navigation/three')]);
    await initRecast();

    // 1) Cache — regeneration is expensive on phones (~seconds).
    const cacheKey = CACHE_PREFIX + config.code;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { navMesh: nm } = importNavMesh(base64ToBytes(cached));
        navMeshInstance = nm;
        query = new NavMeshQuery(nm);
        console.info('[NavMe] Navmesh restored from cache');
        logNavmesh('cache');
        return true;
      }
    } catch {
      /* cache miss/corrupt — regenerate below */
    }

    // 2) Authored navmesh, if provided.
    let meshes = await loadGlbMeshes('/navmesh.glb').catch(() => [] as THREE.Mesh[]);
    let source = 'authored public/navmesh.glb';

    // 3) Auto-generate from the MultiSet scan mesh.
    if (meshes.length === 0) {
      const rest = new MultisetRestClient(config);
      const meshInfo = await rest.getMapMeshUrl();
      if (!meshInfo) {
        console.info('[NavMe] No navmesh source (no authored file, no scan mesh) — graph/direct routing only');
        return false;
      }
      if (meshInfo.format !== 'glb') {
        console.info('[NavMe] Scan mesh is OBJ — navmesh auto-generation currently supports GLB only');
        return false;
      }
      meshes = await loadGlbMeshes(meshInfo.url);
      source = 'MultiSet scan mesh (auto-generated)';
      if (meshes.length === 0) return false;
    }

    const t0 = performance.now();
    const { success, navMesh: nm } = threeToSoloNavMesh(meshes, GENERATION_CONFIG);
    if (!success || !nm) {
      console.warn('[NavMe] Navmesh generation failed — falling back to graph/direct routing');
      return false;
    }
    navMeshInstance = nm;
    query = new NavMeshQuery(nm);
    console.info(
      `[NavMe] Navmesh ready from ${source} in ${Math.round(performance.now() - t0)} ms`,
    );
    logNavmesh(source, performance.now() - t0);

    try {
      localStorage.setItem(cacheKey, bytesToBase64(exportNavMesh(nm)));
    } catch {
      /* quota exceeded — fine, regenerate next launch */
    }
    return true;
  } catch (err) {
    console.warn('[NavMe] Navmesh unavailable:', err);
    query = null;
    return false;
  }
}

/** Clears the cached navmesh for a map (call after re-scanning a venue). */
export function invalidateNavmeshCache(mapCode: string): void {
  localStorage.removeItem(CACHE_PREFIX + mapCode);
}

export function isNavmeshLoaded(): boolean {
  return query !== null;
}

/**
 * Raw walkable-surface triangles for debug visualization (Dashboard's
 * "Navmesh" overlay). Returns null when no navmesh is loaded.
 */
export function getNavmeshDebugData(): { positions: number[]; indices: number[] } | null {
  if (!navMeshInstance) return null;
  // recast-navigation v0.35: debug triangles come from the NavMesh itself.
  const [positions, indices] = navMeshInstance.getDebugNavMesh();
  return { positions, indices };
}

/**
 * Snaps an arbitrary point to the nearest point ON the walkable surface —
 * on the SAME floor. Unconstrained snapping punched through floor slabs
 * (debug 2026-07-12 12:29Z: user on floor 2, start snapped to a floor-1
 * polygon 3.1 m below → "navmesh 2 nodes 6.0m" route ran one floor under
 * the user, crumbs invisible below the slab). `maxDyM` rejects any snap
 * that moves the point vertically by more than roughly half a storey.
 * Returns null when no navmesh is loaded or nothing is close enough.
 */
export function snapToNavmesh(p: Vec3, maxDyM = 2.0): Vec3 | null {
  if (!query) return null;
  let r: { success: boolean; point: Vec3 };
  try {
    // recast-navigation supports a search box; constrain it vertically so the
    // nearest-poly lookup can't even SEE the floor below/above.
    r = query.findClosestPoint(p, { halfExtents: { x: 2, y: maxDyM, z: 2 } });
  } catch {
    r = query.findClosestPoint(p); // older API — validated below
  }
  if (!r.success) return null;
  // Belt and braces: even if halfExtents was ignored, reject cross-floor snaps.
  if (Math.abs(r.point.y - p.y) > maxDyM) return null;
  return { x: r.point.x, y: r.point.y, z: r.point.z };
}

/** Detailed navmesh path result — PARTIAL paths are returned (not hidden)
 *  so callers can gap-hop across scan holes (routeService.tryGapHops). */
export interface NavmeshPathResult {
  path: Vec3[];
  /** True when the path actually reaches the destination. */
  complete: boolean;
  /** How far the path's last point falls short of the target (m). */
  endGapXZ: number;
  endGapY: number;
}

/**
 * Walkable path from the user's position toward a target, or null when no
 * navmesh is loaded or the start/end can't snap to their OWN floor. The
 * start snap is vertically constrained (an unconstrained snap punched
 * through the slab — route ran one floor below the user, 2026-07-12), and
 * per-segment through-slab teleports are rejected. A path that stops short
 * (disconnected islands: stairwell scan holes, dark doorways) is returned
 * with complete:false so routing can bridge/hop it instead of dead-ending.
 */
export function findNavmeshPathEx(fromCamera: Vec3, to: Vec3): NavmeshPathResult | null {
  if (!query) return null;

  const foot = { x: fromCamera.x, y: fromCamera.y - CAMERA_HEIGHT_M, z: fromCamera.z };
  const start = snapToNavmesh(foot, 1.5);
  if (!start) {
    logEvent(
      'route',
      `navmesh: no walkable surface within 1.5m of user's floor (footY=${foot.y.toFixed(2)}) — this floor isn't covered; falling through`,
    );
    return null;
  }
  const end = snapToNavmesh(to, 2.0);
  if (!end) {
    logEvent('route', 'navmesh: destination has no walkable surface on its own floor — falling through');
    return null;
  }

  const { success, path } = query.computePath(start, end);
  if (!success || !path || path.length === 0) return null;

  // Through-slab teleport within the path itself = broken data — reject.
  for (let i = 1; i < path.length; i++) {
    const dy = Math.abs(path[i].y - path[i - 1].y);
    const h = Math.hypot(path[i].x - path[i - 1].x, path[i].z - path[i - 1].z);
    if (dy > 1.5 && dy > h) {
      logEvent(
        'route',
        `navmesh path rejected: vertical jump ${dy.toFixed(1)}m over ${h.toFixed(1)}m at segment ${i} — through-slab teleport; falling through`,
      );
      return null;
    }
  }

  const last = path[path.length - 1];
  const endGapXZ = Math.hypot(last.x - end.x, last.z - end.z);
  const endGapY = Math.abs(last.y - end.y);
  const complete = endGapXZ <= 2.5 && endGapY <= 1.5;
  return {
    path: path.map((p) => ({ x: p.x, y: p.y, z: p.z })),
    complete,
    endGapXZ,
    endGapY,
  };
}

/** Strict variant: a walkable path that REACHES the target, or null. */
export function findNavmeshPath(fromCamera: Vec3, to: Vec3): Vec3[] | null {
  const r = findNavmeshPathEx(fromCamera, to);
  if (!r) return null;
  if (!r.complete) {
    logEvent(
      'route',
      `navmesh path is partial (ends ${r.endGapXZ.toFixed(1)}m / Δy ${r.endGapY.toFixed(1)}m from destination) — islands disconnected`,
    );
    return null;
  }
  return r.path;
}
